import { Alert, Button, Card, Checkbox, FormField, Input } from '@intelliauth/ui'
import { Eye, EyeOff, Mail } from 'lucide-react'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { AuthLayout } from '@/components/AuthLayout'
import { useAuth } from '@/contexts/AuthContext'
import { CpV2ApiError, login } from '@/lib/api/cpClient'

export default function LoginPage() {
  const navigate = useNavigate()
  const { refreshSession } = useAuth()

  const [justRegistered] = useState(() => {
    const val = sessionStorage.getItem('justRegistered')
    if (val) sessionStorage.removeItem('justRegistered')
    return val === '1'
  })

  const [showPassword, setShowPassword] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setIsLoading(true)
    setError(null)

    const formData = new FormData(e.currentTarget)
    const email = formData.get('email') as string
    const password = formData.get('password') as string

    try {
      const res = await login({ email, password })
      // Email-verification gate: cp-api returns 200 with
      // {verification_required:true, email} when credentials are valid
      // but the org-admin's primary email is unconfirmed. No access
      // token is issued, no refresh cookie is set, and AuthContext is
      // left empty. Bounce the user to the verification-sent page,
      // which renders "Check your email" + a Resend button against
      // /api/v1/auth/verify/request — the same screen the signup
      // flow lands on, so the flow stays consistent regardless of
      // whether the user reached the gate by signing up fresh or by
      // attempting a login on an unverified account.
      const gated = res as unknown as { verification_required?: boolean; email?: string }
      if (gated?.verification_required) {
        navigate(`/verify-email/sent?email=${encodeURIComponent(gated.email ?? email)}`)
        return
      }
      // Token now lives in the cpClient singleton; refreshSession()
      // calls /api/v1/auth/session with it and populates AuthContext.
      await refreshSession()
      navigate('/dashboard')
    } catch (err) {
      // CpV2ApiError carries status + error.code; map to user-visible
      // copy. Generic credential-failure copy on 401 is anti-enumeration
      // (matches the backend's anti-enumeration message).
      if (err instanceof CpV2ApiError) {
        if (err.status === 401) {
          setError('Invalid email or password. Please try again.')
        } else if (err.status === 422) {
          setError('Please enter a valid email and password.')
        } else if (err.status === 503) {
          setError('Authentication service is unavailable. Please retry in a moment.')
        } else {
          setError('Sign-in failed. Please try again.')
        }
      } else {
        setError('Sign-in failed. Please try again.')
      }
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <AuthLayout>
      <Card className="p-8">
        <div className="mb-6">
          <h2 className="text-2xl font-semibold text-foreground">Sign in to your account</h2>
          <p className="mt-1.5 text-sm text-muted-foreground">Enter your credentials to continue</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {justRegistered && <Alert tone="success">Account created. Sign in to continue.</Alert>}

          {error && <Alert tone="error">{error}</Alert>}

          <FormField label="Email address" htmlFor="email">
            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                className="pl-10"
                placeholder="you@company.com"
              />
            </div>
          </FormField>

          <FormField label="Password" htmlFor="password">
            <div className="relative">
              <Input
                id="password"
                name="password"
                type={showPassword ? 'text' : 'password'}
                autoComplete="current-password"
                required
                className="pr-10"
                placeholder="Enter your password"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </FormField>

          <div className="flex items-center justify-between">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox name="remember-me" />
              <span className="text-sm text-muted-foreground">Remember me</span>
            </label>
            <button
              type="button"
              onClick={() => toast.info('Password recovery is coming soon.')}
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Forgot password?
            </button>
          </div>

          <Button type="submit" loading={isLoading} className="w-full">
            Sign in
          </Button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-muted-foreground">
            Don't have an account?{' '}
            <Link
              to="/register"
              className="font-medium text-primary hover:text-primary/80 transition-colors"
            >
              Sign up
            </Link>
          </p>
        </div>
      </Card>

      <div className="mt-6 text-center text-xs text-muted-foreground/60">
        <a href="#" className="hover:text-muted-foreground">
          Terms of Service
        </a>{' '}
        &middot;{' '}
        <a href="#" className="hover:text-muted-foreground">
          Privacy Policy
        </a>
      </div>
    </AuthLayout>
  )
}
